import { useEffect, useState } from 'react'

import { t } from '../../../../../common/i18n.js'
import type { Language } from '../../../../../common/types/i18n.js'
import { formatDisplayDate } from '../../../../../common/utils/format-utils.js'
import type {
    MyTasksPayload,
    NotionTaskPreview,
} from '../../../../../server/controllers/portal/dashboard-card-payloads.js'
import { CheckSquare } from '../../../../components/icon/solar-icons.js'
import {
    PortalDataTable,
    type PortalDataTableColumn,
} from '../../../../components/portal-data-table/portal-data-table.js'
import { PortalEmptyState } from '../../../../components/portal-empty-state/portal-empty-state.js'
import {
    PriorityPill,
    StatusPill,
} from '../../../../components/portal-task-pill/portal-task-pill.js'
import { Skeleton } from '../../../../components/skeleton/skeleton.js'
import { portalGet } from '../../utils/api.js'
import './tasks-view.css'

interface TasksViewProps {
    lang: Language
}

// Read-only view of the current user's open tasks from the shared Notion
// "Tasklist" database (same source as the dashboard "Notion taskid" card),
// rendered Notion-style: one app-styled table per Project group. Nothing here
// writes back to Notion.
export const TasksView = ({ lang }: TasksViewProps) => {
    const [payload, setPayload] = useState<MyTasksPayload | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        void (async () => {
            const result = await portalGet<MyTasksPayload>('/api/portal/me/notion-tasks')
            if (!cancelled && result.ok) setPayload(result.data)
            if (!cancelled) setLoading(false)
        })()
        return () => {
            cancelled = true
        }
    }, [])

    const c = t.portal.dashboard.tasks

    if (loading || !payload) {
        return (
            <div className="portal-tasks">
                <section className="portal-tasks__group">
                    <Skeleton width={180} height={20} />
                    <div className="portal-tasks__skeleton-rows">
                        <Skeleton count={5} height={44} />
                    </div>
                </section>
            </div>
        )
    }

    if (payload.status === 'not_configured') {
        return <PortalEmptyState icon={<CheckSquare size={32} />} message={c.notConfigured(lang)} />
    }
    if (payload.status === 'no_account') {
        return <PortalEmptyState icon={<CheckSquare size={32} />} message={c.noAccount(lang)} />
    }
    if (payload.status === 'error') {
        return <PortalEmptyState icon={<CheckSquare size={32} />} message={c.error(lang)} />
    }
    if (payload.groups.length === 0) {
        return <PortalEmptyState icon={<CheckSquare size={32} />} message={c.empty(lang)} />
    }

    const openTask = (task: NotionTaskPreview) => {
        if (task.url) window.open(task.url, '_blank', 'noreferrer')
    }

    const columns: PortalDataTableColumn<NotionTaskPreview>[] = [
        {
            key: 'title',
            header: c.colTask(lang),
            truncate: true,
            render: (task) =>
                task.url ? (
                    <a
                        className="portal-tasks__task-link"
                        href={task.url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {task.title || c.untitled(lang)}
                    </a>
                ) : (
                    task.title || c.untitled(lang)
                ),
        },
        // Fixed widths on the short columns: each project group is its own
        // <table>, so without them every table sized Olek/Tähtaeg/Prioriteet
        // by its own content and the columns zig-zagged down the page.
        {
            key: 'status',
            header: c.colStatus(lang),
            width: 160,
            render: (task) => <StatusPill status={task.status} />,
        },
        {
            key: 'due',
            header: c.colDue(lang),
            width: 140,
            render: (task) => (task.dueDate ? formatDisplayDate(task.dueDate, 'short', lang) : '—'),
        },
        {
            key: 'priority',
            header: c.colPriority(lang),
            width: 140,
            render: (task) => <PriorityPill priority={task.priority} lang={lang} />,
        },
    ]

    return (
        <div className="portal-tasks">
            {payload.groups.map((group) => (
                <section key={group.project?.id ?? '__other__'} className="portal-tasks__group">
                    <h3 className="portal-tasks__group-title">
                        {group.project ? group.project.name : c.otherGroup(lang)}
                    </h3>
                    <PortalDataTable<NotionTaskPreview>
                        columns={columns}
                        rows={group.tasks}
                        rowKey={(task) => task.id}
                        onRowClick={openTask}
                    />
                </section>
            ))}
        </div>
    )
}
